// Full-screen company auth view — the front door. One entry shell (the Spark
// and wordmark in a 56 px top bar, one text action on the right), a
// top-anchored column, visible labels and per-field validation. Rendered as
// SPA state (no router exists); mounted permanently and shown when `open`;
// signed-out company workspaces are hidden behind it, while an authenticated
// recovery can still be dismissed.

import { useEffect, useRef, useState } from 'react';
import {
  AuthError,
  resendSignupVerification,
  signup as serverSignup,
  login as serverLogin,
} from '@/server-threads';
import type { SessionUser } from '@/server-threads';
import { formatCopy } from '@/i18n/ns';
import { isCoarsePointer } from '@/lib/mobile';
import { authLangFor } from './strings';
import { entryCopyFor, entryErrorText } from './entry-copy';
import { EntryAlert } from './entry-field';
import { EntryHeading, EntryShell } from './entry-shell';
import { EmailSentState } from './email-sent-state';
import { CredentialsForm } from './credentials-form';
import { VerificationCompleting, VerificationConfirmPassword } from './invite-gate';
import { PasswordRecovery } from './password-recovery';
import { GoogleSignInPending } from './google-sign-in-pending';
import { InvitationState, useCompanyInvitation } from './company-invitation';
import {
  clearField,
  fieldForServerCode,
  hasErrors,
  loginErrors,
  signupErrors,
  type EntryFieldName,
  type FieldErrors,
} from './entry-validation';

export const accountLinkNeedsLogin = (error: string | null) => error === 'account_link_required';

type Screen = 'signup' | 'login' | 'forgot' | 'reset';

function screenFor(
  resetToken: string | null,
  initialError: string | null,
  initialScreen: 'signup' | 'login',
): Screen {
  if (resetToken) return 'reset';
  if (accountLinkNeedsLogin(initialError)) return 'login';
  if (initialError === 'company_name_required') return 'signup';
  return initialScreen;
}

export function AuthScreen({
  open,
  onClose,
  onAuthed,
  providers,
  verifying,
  confirmVerify,
  initialError,
  locale,
  inviteCode,
  onGoogle,
  googlePending = false,
  resetToken,
  onPasswordReset,
  initialScreen,
}: {
  open: boolean;
  onClose?: () => void;
  onAuthed: (u: SessionUser) => void;
  providers: string[];
  verifying: boolean;
  /**
   * Verification link opened outside the signup browser: asks for the chosen
   * password and finishes signup with it (null when not in that state).
   */
  confirmVerify: ((password: string) => Promise<void>) | null;
  /** Stable error key from a failed OAuth redirect (?auth_error=...). */
  initialError: string | null;
  locale: string;
  inviteCode: string | null;
  onGoogle: (companyName?: string) => void;
  googlePending?: boolean;
  /** Fragment-only recovery token, removed from the address bar at boot. */
  resetToken: string | null;
  onPasswordReset: () => void;
  initialScreen: 'signup' | 'login';
}) {
  const language = authLangFor(locale);
  const { entry, auth: t } = entryCopyFor(locale);
  const [screen, setScreen] = useState<Screen>(screenFor(resetToken, initialError, initialScreen));
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [password, setPassword] = useState('');
  const [fields, setFields] = useState<FieldErrors>({});
  // A form-level outcome code (localized through entryErrorText).
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [inviteRejected, setInviteRejected] = useState(false);
  // "Create a company" from an unusable invitation drops the invitation.
  const [ignoreInvite, setIgnoreInvite] = useState(false);
  // "Password changed" offers "Continue to log in" as its primary.
  const [resetDone, setResetDone] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const companyRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const activeInvite = ignoreInvite ? null : inviteCode;
  const invitation = useCompanyInvitation({ open, token: activeInvite });
  const boundRecipient = invitation.data?.recipient ?? null;

  // Seed the OAuth error when the screen opens after a failed redirect; clear
  // stale state whenever it closes.
  useEffect(() => {
    if (open) {
      const field = initialError ? fieldForServerCode(initialError) : null;
      setFields(field && initialError ? { [field]: initialError } : {});
      setError(field ? null : initialError);
      setInviteRejected(initialError === 'invalid_invite');
      setScreen(screenFor(resetToken, initialError, initialScreen));
      // A mounted workspace can still hold focus when the screen opens (its
      // autofocus effect runs after the first field's autoFocus commit;
      // in-app WebViews that misreport a fine pointer trigger it even on
      // phones). Reclaim: release the keyboard on touch, move focus into the
      // form everywhere else.
      const active = document.activeElement;
      if (active instanceof HTMLElement && !rootRef.current?.contains(active)) {
        if (isCoarsePointer()) active.blur();
        else (companyRef.current ?? emailRef.current ?? passwordRef.current)?.focus();
      }
    } else {
      setFields({});
      setError(null);
      setBusy(false);
      setPendingEmail(null);
      setInviteRejected(false);
      setIgnoreInvite(false);
      setResetDone(false);
      setScreen(initialScreen);
    }
  }, [open, initialError, resetToken, initialScreen]);

  // ESC closes, same as the dialog it replaces.
  useEffect(() => {
    if (!open || !onClose) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Autofocus pops the keyboard on touch devices — desktop only.
  const autoFocusFirstField = !isCoarsePointer();
  const signupMode = screen === 'signup';
  const creatingCompany = signupMode && !activeInvite;
  const gateOnly = signupMode && Boolean(activeInvite) && (inviteRejected || !invitation.data);
  const showGoogle = providers.includes('google') && !gateOnly;
  const recoveryMode = screen === 'forgot' || screen === 'reset';

  const go = (next: Screen) => {
    setScreen(next);
    setFields({});
    setError(null);
    setResetDone(false);
  };
  const change = (field: EntryFieldName, value: string) => {
    if (field === 'company') setCompanyName(value);
    else if (field === 'email') setEmail(value);
    else setPassword(value);
    setFields((current) => clearField(current, field));
    setError(null);
  };
  const focusFirstInvalid = (found: FieldErrors) => {
    const target = found.company ? companyRef : found.email ? emailRef : passwordRef;
    target.current?.focus();
  };

  const applyServerError = (code: string) => {
    if (code === 'invalid_invite') setInviteRejected(true);
    const normalized = code === 'invalid_input' ? 'email_invalid' : code;
    const field = fieldForServerCode(normalized);
    const shown =
      field && !(field === 'email' && boundRecipient) && !(field === 'company' && !creatingCompany);
    if (shown) setFields({ [field]: normalized });
    else setError(code === 'invalid_input' ? 'generic' : code);
  };

  const submit = async () => {
    if (signupMode && activeInvite && (!boundRecipient || invitation.loading || invitation.error))
      return;
    const emailState = boundRecipient
      ? null
      : { value: email, malformed: Boolean(emailRef.current?.validity?.typeMismatch) };
    const found = signupMode
      ? signupErrors({ company: creatingCompany ? companyName : null, email: emailState, password })
      : loginErrors({ email: emailState, password });
    setFields(found);
    setError(null);
    if (hasErrors(found)) {
      focusFirstInvalid(found);
      return;
    }
    setBusy(true);
    try {
      if (signupMode) {
        const pending = await serverSignup(
          (boundRecipient ?? email).trim(),
          password,
          activeInvite ?? undefined,
          creatingCompany ? companyName.trim() : undefined,
          language,
        );
        setPendingEmail(pending.email);
      } else {
        onAuthed(
          await serverLogin(
            (boundRecipient ?? email).trim(),
            password,
            accountLinkNeedsLogin(initialError),
          ),
        );
      }
    } catch (e) {
      applyServerError(e instanceof AuthError ? e.code : 'generic');
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    if (creatingCompany && !companyName.trim()) {
      setFields({ company: 'company_name_required' });
      companyRef.current?.focus();
      return;
    }
    onGoogle(creatingCompany ? companyName.trim() : undefined);
  };

  const invitationState =
    inviteRejected || invitation.error === 'invalid'
      ? 'invalid'
      : invitation.error
        ? 'unavailable'
        : 'loading';

  // The one text action in the top bar: the other way in. An unusable
  // invitation and "Password changed" already offer "Log in" as their primary,
  // so the bar stays empty there.
  const primaryIsLogin = resetDone || (gateOnly && invitationState === 'invalid');
  const action =
    googlePending || verifying || confirmVerify || pendingEmail || primaryIsLogin
      ? null
      : screen === 'login'
        ? { label: t.switchToSignup, onClick: () => go('signup') }
        : { label: t.switchToLogin, onClick: () => go('login') };

  const title = signupMode
    ? activeInvite && invitation.data
      ? formatCopy(entry.inviteTitle, { company: invitation.data.company.name })
      : entry.signupTitle
    : entry.loginTitle;

  let body;
  if (googlePending) body = <GoogleSignInPending locale={locale} />;
  else if (verifying) body = <VerificationCompleting copy={t} />;
  else if (confirmVerify)
    body = <VerificationConfirmPassword copy={t} entry={entry} onConfirm={confirmVerify} />;
  else if (pendingEmail)
    body = (
      <EmailSentState
        copy={entry}
        lead={entry.sentTo}
        email={pendingEmail}
        hint={activeInvite ? entry.inviteSentHint : entry.signupSentHint}
        resend={{
          send: () => resendSignupVerification(pendingEmail, language),
          errorText: (code) =>
            code === 'verification_expired'
              ? entry.signupExpired
              : code === 'rate_limited'
                ? entryErrorText(code, entry, t)
                : entry.resendFailed,
        }}
        back={{
          label: activeInvite ? entry.backToInvite : entry.useAnotherEmail,
          onClick: () => setPendingEmail(null),
        }}
      />
    );
  else if (recoveryMode)
    body = (
      <PasswordRecovery
        key={`${screen}:${resetToken ?? ''}`}
        mode={screen === 'reset' ? 'reset' : 'forgot'}
        initialEmail={boundRecipient ?? email}
        token={resetToken}
        locale={language}
        entry={entry}
        auth={t}
        onResetComplete={() => {
          setResetDone(true);
          onPasswordReset();
        }}
        onBack={() => go('login')}
      />
    );
  else if (gateOnly)
    body = (
      <>
        <InvitationState
          copy={entry}
          loginLabel={t.switchToLogin}
          state={invitationState}
          onRetry={invitation.retry}
          onLogin={() => go('login')}
          onCreateCompany={() => {
            setIgnoreInvite(true);
            setInviteRejected(false);
            go('signup');
          }}
        />
        {error && error !== 'invalid_invite' ? (
          <div className="pc-entry-actions">
            <EntryAlert>{entryErrorText(error, entry, t)}</EntryAlert>
          </div>
        ) : null}
      </>
    );
  else
    body = (
      <>
        <EntryHeading title={title} lead={creatingCompany ? entry.signupLead : null} />
        <CredentialsForm
          mode={signupMode ? 'signup' : 'login'}
          creatingCompany={creatingCompany}
          boundRecipient={boundRecipient}
          showGoogle={showGoogle}
          values={{ companyName, email, password }}
          fields={fields}
          error={error}
          busy={busy}
          autoFocusFirstField={autoFocusFirstField}
          refs={{ company: companyRef, email: emailRef, password: passwordRef }}
          entry={entry}
          auth={t}
          onChange={change}
          onSubmit={() => void submit()}
          onGoogle={google}
          onForgot={() => go('forgot')}
        />
      </>
    );

  return (
    <EntryShell
      layer="auth"
      rootRef={rootRef}
      action={action}
      close={onClose && !googlePending ? { label: t.close, onClick: onClose } : null}
      wash
    >
      {body}
    </EntryShell>
  );
}
